import { useCallback, useEffect, useMemo, useState } from "react";
import {
    UserPlus,
    Users,
    Search,
    MoreHorizontal,
    Pencil,
    KeyRound,
    UserX,
    UserCheck,
    Trash2,
    Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/layout/PageHeader";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineLoader } from "@/components/common/Loader";
import UserFormDialog from "@/components/admin/UserFormDialog";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

// Role -> badge classes. Grouped by "family" so the 13 roles stay readable:
// orange = admin/manager (super/ops/QM), sky = QC operational, green =
// branch/module managers, muted = infra/user.
const _ORANGE = "bg-[color:rgba(209,128,47,0.16)] text-[color:var(--brand-brown)] border-0 self-start";
const _SKY = "bg-[color:rgba(83,143,181,0.24)] text-[color:var(--brand-brown)] border-0 self-start";
const _GREEN = "bg-[color:rgba(31,122,77,0.14)] text-[color:var(--success)] border-0 self-start";
const _MUTED = "bg-[color:rgba(186,217,235,0.5)] text-[color:var(--ink)] border-0 self-start";
const ROLE_BADGE_CLASS = {
    super_admin: _ORANGE,
    ops_manager: _ORANGE,
    quality_manager: _ORANGE,
    qc_supervisor: _SKY,
    inspector: _SKY,
    complaints_manager: _SKY,
    branch_manager: _GREEN,
    warehouse_manager: _GREEN,
    kitchen_manager: _GREEN,
    maintenance_manager: _GREEN,
    call_center_manager: _GREEN,
    it_admin: _MUTED,
    user: _MUTED,
};

function fmtDate(iso, lang) {
    if (!iso) return null;
    try {
        return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-US", {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(new Date(iso));
    } catch {
        return iso;
    }
}

export default function UsersPage() {
    const { me, lang } = useAuth();
    const { t, lang: language } = useI18n();
    const myId = me?.user?.id;

    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [scLocations, setScLocations] = useState([]);   // «الجرد الشهري» locations (own fetch below)
    const [registry, setRegistry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const branchNameLookup = useMemo(() => {
        const m = {};
        (branches || []).forEach((b) => {
            m[b.id] = language === "ar" ? (b.name_ar || b.name_en || b.id) : (b.name_en || b.name_ar || b.id);
        });
        return m;
    }, [branches, language]);

    const [formOpen, setFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [resetUser, setResetUser] = useState(null);
    const [resetPwd, setResetPwd] = useState("");
    const [resetting, setResetting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchUsers = useCallback(async () => {
        const r = await api.get("/users");
        setUsers(r.data);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [u, b, reg] = await Promise.all([
                    api.get("/users"),
                    api.get("/branches"),
                    api.get("/config/registry"),
                ]);
                setUsers(u.data);
                setBranches(b.data);
                setRegistry(reg.data);
            } catch (e) {
                toast.error(t("error_generic"));
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    // «الجرد الشهري» locations for the user form's «مواقع الجرد» picker —
    // fetched OUTSIDE the Promise.all above, failing quietly to [], so a 403
    // or an older server never turns the whole page into an error.
    useEffect(() => {
        let alive = true;
        api.get("/sc/locations")
            .then((r) => {
                if (alive) setScLocations(Array.isArray(r.data) ? r.data : r.data?.locations || []);
            })
            .catch(() => {
                if (alive) setScLocations([]);
            });
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.national_id || "").includes(q)
        );
    }, [users, search]);

    const openCreate = () => {
        setEditingUser(null);
        setFormOpen(true);
    };
    const openEdit = (u) => {
        setEditingUser(u);
        setFormOpen(true);
    };

    const toggleActive = async (u) => {
        try {
            await api.post(`/users/${u.id}/${u.is_active ? "deactivate" : "activate"}`);
            toast.success(t("action_success"));
            fetchUsers();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    const doReset = async () => {
        if (resetPwd.length < 6) return toast.error(t("password_hint"));
        setResetting(true);
        try {
            await api.post(`/users/${resetUser.id}/reset-password`, { new_password: resetPwd });
            toast.success(t("save_success"));
            setResetUser(null);
            setResetPwd("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setResetting(false);
        }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/users/${deleteTarget.id}`);
            toast.success(t("action_success"));
            setDeleteTarget(null);
            fetchUsers();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
            setDeleteTarget(null);
        }
    };

    return (
        <div data-testid={OPS.users.page} className="space-y-5">
            <PageHeader
                icon={Users}
                title={t("users_title")}
                subtitle={t("users_subtitle")}
                primaryAction={
                    <Button
                        data-testid={OPS.users.add}
                        onClick={openCreate}
                        className="gap-2"
                    >
                        <UserPlus className="h-4 w-4" />
                        {t("add_user")}
                    </Button>
                }
                actions={
                    <div className="relative w-full lg:w-[280px]">
                        <Search className="absolute inset-inline-start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--ink-muted)]" />
                        <Input
                            data-testid={OPS.users.search}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("search_users")}
                            className="ps-9"
                        />
                    </div>
                }
            />

            <div className="rounded-xl surface border border-[color:var(--border-soft)] overflow-hidden">
                {loading ? (
                    <InlineLoader label={t("loading")} />
                ) : (
                    <div className="overflow-x-auto">
                        <Table data-testid={OPS.users.table}>
                            <TableHeader>
                                <TableRow className="bg-[color:rgba(224,222,217,0.55)] hover:bg-[color:rgba(224,222,217,0.55)]">
                                    <TableHead className="text-start">{t("name")}</TableHead>
                                    <TableHead className="text-start">{t("email")} / {t("national_id")}</TableHead>
                                    <TableHead className="text-start">{t("role")}</TableHead>
                                    <TableHead className="text-start">{t("status")}</TableHead>
                                    <TableHead className="text-start">{t("last_login")}</TableHead>
                                    <TableHead className="text-end">{t("actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-10 text-[color:var(--ink-muted)]">
                                            {t("no_users")}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered.map((u) => (
                                        <TableRow key={u.id} className="hover:bg-[color:rgba(186,217,235,0.20)]">
                                            <TableCell className="font-medium text-[color:var(--ink)]">{u.name}</TableCell>
                                            <TableCell className="text-[color:var(--ink-muted)]">
                                                <bdi dir="ltr">{u.email || u.national_id}</bdi>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-1">
                                                    <Badge
                                                        data-testid={OPS.users.roleBadge(u.id)}
                                                        variant="secondary"
                                                        className={ROLE_BADGE_CLASS[u.role] || _MUTED}
                                                    >
                                                        {t(`role_${u.role}`) || t("role_user")}
                                                    </Badge>
                                                    {u.role === "branch_manager" && u.assigned_branch_id ? (
                                                        <span
                                                            data-testid={OPS.users.assignedBranchLabel(u.id)}
                                                            className="text-[10px] text-[color:var(--ink-muted)] max-w-[180px] truncate"
                                                            title={branchNameLookup[u.assigned_branch_id] || u.assigned_branch_id}
                                                        >
                                                            {branchNameLookup[u.assigned_branch_id] || u.assigned_branch_id}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1.5 text-xs">
                                                    <span
                                                        className={`h-2 w-2 rounded-full ${
                                                            u.is_active
                                                                ? "bg-[color:var(--success)]"
                                                                : "bg-[color:var(--border-strong)]"
                                                        }`}
                                                    />
                                                    {u.is_active ? t("active") : t("inactive")}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-[color:var(--ink-muted)] text-xs">
                                                {fmtDate(u.last_login, language) || t("never")}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            data-testid={OPS.users.rowMenu(u.id)}
                                                            aria-label="actions"
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48">
                                                        <DropdownMenuItem
                                                            data-testid={OPS.users.edit(u.id)}
                                                            onClick={() => openEdit(u)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Pencil className="h-4 w-4 me-2" />
                                                            {t("edit")}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            data-testid={OPS.users.reset(u.id)}
                                                            onClick={() => {
                                                                setResetUser(u);
                                                                setResetPwd("");
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            <KeyRound className="h-4 w-4 me-2" />
                                                            {t("reset_password")}
                                                        </DropdownMenuItem>
                                                        {u.id !== myId ? (
                                                            <DropdownMenuItem
                                                                data-testid={OPS.users.toggle(u.id)}
                                                                onClick={() => toggleActive(u)}
                                                                className="cursor-pointer"
                                                            >
                                                                {u.is_active ? (
                                                                    <>
                                                                        <UserX className="h-4 w-4 me-2" />
                                                                        {t("deactivate")}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <UserCheck className="h-4 w-4 me-2" />
                                                                        {t("activate")}
                                                                    </>
                                                                )}
                                                            </DropdownMenuItem>
                                                        ) : null}
                                                        {u.id !== myId ? (
                                                            <DropdownMenuItem
                                                                data-testid={OPS.users.del(u.id)}
                                                                onClick={() => setDeleteTarget(u)}
                                                                className="cursor-pointer text-[color:var(--danger)] focus:text-[color:var(--danger)]"
                                                            >
                                                                <Trash2 className="h-4 w-4 me-2" />
                                                                {t("delete")}
                                                            </DropdownMenuItem>
                                                        ) : null}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            <UserFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                user={editingUser}
                branches={branches}
                scLocations={scLocations}
                registry={registry}
                onSaved={fetchUsers}
            />

            {/* Reset password */}
            <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t("reset_password")}</DialogTitle>
                        <DialogDescription>{resetUser?.name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5">
                        <Label>{t("new_password")}</Label>
                        <Input
                            type="password"
                            data-testid={OPS.users.resetInput}
                            value={resetPwd}
                            onChange={(e) => setResetPwd(e.target.value)}
                            placeholder={t("password_hint")}
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setResetUser(null)} disabled={resetting}>
                            {t("cancel")}
                        </Button>
                        <Button
                            data-testid={OPS.users.resetConfirm}
                            onClick={doReset}
                            disabled={resetting}
                            className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                        >
                            {resetting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                            {t("confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("delete")} — {deleteTarget?.name}</AlertDialogTitle>
                        <AlertDialogDescription>{t("confirm_delete_user")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            className="bg-[color:var(--danger)] hover:bg-[#9A1C14] text-white"
                        >
                            {t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
